import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonType, type Slide } from '@/api/cms/slider';
import { Button } from '@/components/buttons';
import { ImageComponent } from '@/components/Image';
import { isExternalURL } from '@/helpers/common';
import { Modals } from '@/helpers/modals';
import { useNavigation } from '@/hooks/navigation';
import { useABTestingV2 } from '@/modules/abTesting';
import { AB_TESTS } from '@/modules/abTesting/constants';
import { useGetCommonStore, useModalsStore } from '@/store';
import { blurImagePlaceholder } from '@/theme/placeholders';
import { MAX_DESCRIPTION_SM_LENGTH_AB } from './helpers';
import {
  ScPromotion,
  ScPromotionContent,
  ScPromotionContentBottom,
  ScPromotionContentCenter,
  ScPromotionContentContainer,
  ScPromotionContentTitle,
} from './styled';

export interface IPromotionProps extends Slide {
  loading: 'eager' | 'lazy';
}

export const Promotion = memo(
  ({
    title,
    description,
    subDescription,
    backgroundDesktop,
    backgroundMobile,
    charactersDesktop,
    charactersMobile,
    buttonType,
    buttonLink,
    loading,
  }: IPromotionProps) => {
    const { t } = useTranslation();
    const { navigate } = useNavigation();
    const open = useModalsStore.use.open();
    const useCommonStore = useGetCommonStore();

    const isMobileDevice = useCommonStore.use.isMobileDevice();
    const isAuthenticated = useCommonStore.use.isAuthenticated();

    const { isB: isSearchHidden } = useABTestingV2({
      experiment: AB_TESTS.HIDE_SEARCH_ON_LOBBY,
    });

    const className = (() => {
      if (description.length < MAX_DESCRIPTION_SM_LENGTH_AB) {
        return 'big-fs';
      }
      return '';
    })();

    const handleButtonClick = useCallback(() => {
      switch (buttonType) {
        case ButtonType.Deposit:
          open(Modals.Deposit);
          break;
        default:
          isExternalURL(buttonLink)
            ? window.location.replace(buttonLink)
            : navigate(buttonLink);
      }
    }, [buttonType, open, buttonLink, navigate]);

    const bgSrc = isMobileDevice
      ? backgroundMobile?.url
      : backgroundDesktop?.url;
    const charactersSrc = isMobileDevice
      ? charactersMobile?.url
      : charactersDesktop?.url;
    const isDepositButton = buttonType === ButtonType.Deposit;

    return (
      <ScPromotion small={isSearchHidden}>
        <ImageComponent
          alt="slider bg"
          blurDataURL={blurImagePlaceholder}
          className="slider-bg"
          fill
          loading={loading}
          placeholder="blur"
          src={bgSrc || ''}
        />
        <ScPromotionContentContainer>
          <ScPromotionContent>
            <ScPromotionContentTitle
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <ScPromotionContentCenter className={className}>
              <div
                className="description"
                dangerouslySetInnerHTML={{ __html: description }}
              />
              {subDescription && (
                <div
                  className="subDescription"
                  dangerouslySetInnerHTML={{ __html: subDescription }}
                />
              )}
            </ScPromotionContentCenter>
            <ScPromotionContentBottom>
              {isDepositButton && !isAuthenticated ? (
                <Button
                  className="button"
                  onClick={() => open(Modals.Registration)}
                  primary
                  small
                >
                  {t('button.register')}
                </Button>
              ) : (
                <Button
                  className="button"
                  onClick={handleButtonClick}
                  primary
                  small
                >
                  {t(`button.${buttonType}`)}
                </Button>
              )}
            </ScPromotionContentBottom>
          </ScPromotionContent>
          <div className="characters">
            <ImageComponent
              alt="characters"
              className="swiper-lazy"
              fill
              loading={loading}
              src={charactersSrc || ''}
            />
          </div>
          <div className="swiper-lazy-preloader" />
        </ScPromotionContentContainer>
      </ScPromotion>
    );
  }
);
